'use client';

import { useEffect, useRef } from 'react';

interface AnimateInProps {
  children: React.ReactNode;
  className?: string;
  delay?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  threshold?: number;
}

/**
 * Wraps children and fades them up into view as they scroll into the viewport.
 * Uses IntersectionObserver + the CSS [data-reveal] / .in-view classes from globals.css.
 */
export function AnimateIn({ children, className = '', delay, threshold = 0.12 }: AnimateInProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add('in-view');
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return (
    <div
      ref={ref}
      data-reveal
      {...(delay ? { 'data-delay': String(delay) } : {})}
      className={className}
    >
      {children}
    </div>
  );
}
